import {
  AUTH_GOOGLE_SHEETS,
  HAS_GOOGLE_SHEETS,
  HAS_VALID_GOOGLE_SHEET_ID
} from '@dtx-company/flow-codegen/src/page/mutations'
import { ActionDataTypes } from '../types'
import {
  AuthGoogleSheetsResponse,
  ContactCollectionForm,
  ValidateGoogleSheetResponse
} from './types'
import { ContactInfoCapture } from '@dtx-company/flow-codegen/src/page/generated.types'
import { CrmThirdParty } from '@dtx-company/true-common/src/types/crmThirdParty'
import { addProtocol } from '../../../components/profile/LinkForm/utils'
import { pageGqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { storeStateIdInLocalStorage } from '../../../containers/settings/CRMDownload/subcomponents/utils'
import { useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'

export function useAuthGoogleSheets(): () => Promise<void> {
  const stateId = useMemo(() => uuid(), [])

  return async (): Promise<void> => {
    try {
      const resp = await pageGqlFetcher<AuthGoogleSheetsResponse>(AUTH_GOOGLE_SHEETS)

      if (!resp?.authorizeGoogleSheets) {
        console.error('Error authenticating Google Sheets ')
      }
      if (typeof window !== 'undefined') {
        storeStateIdInLocalStorage({ crmType: 'gsheets', stateId })

        window.open(
          `${resp?.authorizeGoogleSheets}${encodeURI(`&state=gsheets-${stateId}`)}`,
          '_self'
        )
      }
    } catch (err) {
      console.error('Error authenticating Google sheets', err)
    }
  }
}

export async function hasGoogleSheets(): Promise<boolean> {
  try {
    const resp = await pageGqlFetcher<{ hasGooglesheets: boolean }>(HAS_GOOGLE_SHEETS)
    return resp?.hasGooglesheets ?? false
  } catch (err) {
    console.error('Error accessing Google sheets,', err)
    return false
  }
}

export async function hasValidGooglesheetId(spreadsheetId: string): Promise<boolean> {
  try {
    if (!spreadsheetId) {
      return false
    }
    const resp = await pageGqlFetcher<ValidateGoogleSheetResponse>(HAS_VALID_GOOGLE_SHEET_ID, {
      spreadsheetId: spreadsheetId
    })
    return Boolean(resp.hasValidGooglesheetId)
  } catch (err) {
    console.error('Error validating sheet,', err)
    return false
  }
}

export const getSpreadsheetIdFromURL = (url: string): string =>
  url.replace('https://', '').replace('http://', '').split('/')[3]

export async function writeToZapier(webhookUrl: string, info: ContactInfoCapture): Promise<void> {
  try {
    await fetch(webhookUrl, { method: 'POST', body: JSON.stringify(info) }).then(res => res.json())
  } catch (err) {
    console.error(err)
  }
}

export async function testZapierWebhook(webhookUrl: string): Promise<void> {
  try {
    await fetch(webhookUrl, {
      method: 'POST',
      // Todo: Update this test payload to be dynamic and include the fields that the user
      // has selected to be included in their form
      body: JSON.stringify({
        displayName: 'John Doe',
        email: 'jhon@mail.com',
        phone: '19175551234'
      })
    }).then(res => res.json())
  } catch (err) {
    console.error(err)
    throw err
  }
}

export function useCheckIfGSheetsAuthorized(): { isAuthorized: boolean; checkAuth: () => void } {
  const [isAuthorized, setIsAuthed] = useState(false)
  const checkAuth = async (): Promise<void> => {
    const hasGSheets = await hasGoogleSheets()
    setIsAuthed(hasGSheets)
  }
  useEffect(() => {
    checkAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { isAuthorized, checkAuth }
}

export async function validateSpreadsheetOwnership(
  spreadsheetUrl: string | null,
  setError: (errorStr: string) => void
): Promise<boolean> {
  if (!spreadsheetUrl || spreadsheetUrl.length === 0) {
    return true
  }
  const spreadsheetId = getSpreadsheetIdFromURL(spreadsheetUrl)
  try {
    const resp = await hasValidGooglesheetId(spreadsheetId)
    if (!resp) {
      setError(
        'Error fetching spreadsheet.  Please ensure you have access to this spreadsheet and that the URL is correct.'
      )
      return false
    } else {
      setError('')
      return true
    }
  } catch (e) {
    console.error('Error validating spreadsheet id', e)
    setError(
      'Error fetching spreadsheet.  Please ensure you have access to this spreadsheet and that the URL is correct.'
    )
    return false
  }
}

export const getThirdPartyIntegrations = (
  actionData: ActionDataTypes['contactCollection']
): (CrmThirdParty | null)[] => {
  const crmTypes = []
  if (actionData?.zapier?.enabled) crmTypes.push(CrmThirdParty.ZAPIER)
  if (actionData?.googleSheets?.enabled) crmTypes.push(CrmThirdParty.GOOGLESHEETS)
  if (actionData?.mailChimp?.enabled) crmTypes.push(CrmThirdParty.MAILCHIMP)
  if (actionData?.klaviyo?.enabled) crmTypes.push(CrmThirdParty.KLAVIYO)
  if (actionData?.hubspot?.enabled) crmTypes.push(CrmThirdParty.HUBSPOT)

  return crmTypes
}

export const buildActionData = ({
  fields: contactFields,
  required,
  customTerms,
  widgetDisplayType,
  customFieldLabel,
  customFieldLabel2,
  zapier,
  mailChimp,
  googleSheets,
  klaviyo,
  hubspot,
  salesforce,
  successMessage,
  filledOutFormRequirement
}: ContactCollectionForm): ActionDataTypes['contactCollection'] => {
  const resolvedContactFields = { ...contactFields }
  const resolvedRequiredFields = { ...required }
  // auto-set email field to enabled and required if integrations enabled
  if (klaviyo?.enabled || mailChimp?.enabled) {
    resolvedContactFields.email = true
    resolvedRequiredFields.email = true
  }

  const contactTypes: string[] = []
  Object.entries(resolvedContactFields).forEach(
    ([k, v]) => v && contactTypes.push(k === 'displayName' ? 'dname' : k)
  )
  const requiredFields: string[] = []
  Object.entries(resolvedRequiredFields).forEach(
    ([k, v]) => v && requiredFields.push(k === 'displayName' ? 'dname' : k)
  )

  const pdfIsFile = customTerms?.pdf instanceof File

  const customTermsWithoutPDF = {
    enabled: customTerms.enabled,
    ...(customTerms?.disclaimer && { disclaimer: customTerms?.disclaimer })
  }
  const externalPdf = !pdfIsFile && customTerms?.pdf ? addProtocol(customTerms?.pdf as string) : ''
  let resolvedGoogleSheets = { ...googleSheets }
  if (!googleSheets.enabled) {
    resolvedGoogleSheets = { enabled: false }
  }
  return {
    fields: contactTypes as ActionDataTypes['contactCollection']['fields'],
    widgetDisplayType,
    required: requiredFields as ActionDataTypes['contactCollection']['required'],
    customFieldLabel,
    customFieldLabel2,
    customTerms: customTermsWithoutPDF,
    zapier,
    mailChimp,
    salesforce,
    googleSheets: resolvedGoogleSheets,
    klaviyo,
    hubspot,
    link:
      customTerms?.pdf instanceof File ? URL.createObjectURL(customTerms.pdf) : externalPdf ?? '',
    successMessage,
    filledOutFormRequirement
  }
}
